import React from 'react';
import { useAppStore } from '../../store/useAppStore.ts';
import { Button } from './Button.tsx';
import { Icon } from './Icon.tsx';
import { EmptyState } from './EmptyState.tsx';
import { CostumePiece } from '../../features/costumes/types.ts';
import { Prop } from '../../features/props/types.ts';

interface MaintenanceTabProps {
    type: 'costumes' | 'props';
}

type MaintenanceItem = (CostumePiece | Prop) & {
    displayName: string;
    sceneLabel: string;
    characterLabel: string;
};

/**
 * Shared Maintenance Tab Component
 * Shows items needing repair or laundry for either costumes or props
 */
export const MaintenanceTab: React.FC<MaintenanceTabProps> = ({ type }) => {
    const costumePieces = useAppStore(state => state.costumes?.pieces || []);
    const props = useAppStore(state => state.props || []);
    const characters = useAppStore(state => state.showStructure.characters || []).filter(c => !c.deleted);
    const scenes = useAppStore(state => state.showStructure?.scenes || []).filter(s => !s.deleted);
    
    const updateCostumePiece = useAppStore(state => state.costumesActions?.updateCostumePiece);
    const updateProp = useAppStore(state => state.propsActions?.updateProp);

    // Get character name by ID
    const getCharacterName = (id?: string) => {
        if (!id) return 'Unassigned';
        const char = characters.find(c => c.id === id);
        return char?.name || 'Unknown';
    };

    // Get scene name by ID
    const getSceneName = (id?: string) => {
        if (!id) return 'Unassigned';
        const scene = scenes.find(s => s.id === id);
        return scene?.name || 'Unknown';
    };

    // Build list of items needing maintenance
    const buildMaintenanceItems = (): MaintenanceItem[] => {
        if (type === 'costumes') {
            return costumePieces
                .filter(p => !p.deleted && (p.maintenance?.needsRepair || p.maintenance?.needsLaundry))
                .map(p => ({
                    ...p,
                    displayName: p.description || 'Unnamed Piece',
                    sceneLabel: getSceneName(p.sceneId),
                    characterLabel: getCharacterName(p.characterId),
                }));
        }
            return props
                .filter(p => !p.deleted && (p.maintenance?.needsRepair || p.maintenance?.needsLaundry))
                .map(p => ({
                    ...p,
                    displayName: p.name || 'Unnamed Prop',
                    sceneLabel: getSceneName(p.sceneId),
                    characterLabel: getCharacterName(p.assignedCharacterId),
                }));
    };

    const maintenanceItems = buildMaintenanceItems();
    
    // Split by type
    const needsRepair = maintenanceItems.filter(item => item.maintenance?.needsRepair);
    const needsLaundry = maintenanceItems.filter(item => item.maintenance?.needsLaundry);

    // Handle mark as repaired
    const handleMarkRepaired = (item: MaintenanceItem) => {
        const updates = {
            maintenance: {
                ...item.maintenance,
                needsRepair: false,
                repairNotes: '',
            }
        };
        
        if (type === 'costumes' && updateCostumePiece) {
            updateCostumePiece(item.id, updates);
        } else if (type === 'props' && updateProp) {
            updateProp(item.id, updates);
        }
    };

    // Handle mark as clean
    const handleMarkClean = (item: MaintenanceItem) => {
        const updates = {
            maintenance: {
                ...item.maintenance,
                needsLaundry: false,
            }
        };
        
        if (type === 'costumes' && updateCostumePiece) {
            updateCostumePiece(item.id, updates);
        } else if (type === 'props' && updateProp) {
            updateProp(item.id, updates);
        }
    };

    // Empty state when nothing needs maintenance
    if (maintenanceItems.length === 0) {
        return (
            <div className="flex h-full items-center justify-center p-8">
                <EmptyState
                    icon="check_circle"
                    title="All items in good condition!"
                    description={`No ${type === 'costumes' ? 'costume pieces' : 'props'} currently need repair or cleaning.`}
                />
            </div>
        );
    }

    const renderTable = (
        items: MaintenanceItem[],
        title: string,
        icon: string,
        iconColor: string,
        actionLabel: string,
        onAction: (item: MaintenanceItem) => void,
        showNotes: boolean = false
    ) => {
        if (items.length === 0) return null;

        return (
            <div className="mb-8">
                {/* Section Header */}
                <div className="mb-4 flex items-center gap-2 px-2">
                    <Icon name={icon} className={`text-xl ${iconColor}`} />
                    <h3 className="font-semibold text-gray-900 text-lg dark:text-white">
                        {title}
                    </h3>
                    <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 font-medium text-xs dark:bg-gray-700">
                        {items.length}
                    </span>
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-border-dark dark:bg-surface-dark">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-800/50">
                            <tr>
                                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                    {type === 'costumes' ? 'Piece' : 'Prop'}
                                </th>
                                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                    Scene
                                </th>
                                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                    Character
                                </th>
                                {showNotes ? <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                        Notes
                                    </th> : null}
                                <th className="w-32 px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-300">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-border-dark">
                            {items.map(item => (
                                <tr 
                                    key={item.id}
                                    className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/30"
                                >
                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                        {item.displayName}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                                        {item.sceneLabel}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                                        {item.characterLabel}
                                    </td>
                                    {showNotes ? <td className="max-w-xs truncate px-4 py-3 text-gray-600 dark:text-gray-400">
                                            {item.maintenance?.repairNotes || '—'}
                                        </td> : null}
                                    <td className="px-4 py-3 text-right">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => onAction(item)}
                                            leftIcon="check"
                                        >
                                            {actionLabel}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="h-full overflow-y-auto p-4 lg:p-6">
            {/* Needs Repair Section */}
            {renderTable(
                needsRepair,
                'Needs Repair',
                'build',
                'text-red-500',
                'Mark Repaired',
                handleMarkRepaired,
                true // show repair notes
            )}

            {/* Needs Laundry Section */}
            {renderTable(
                needsLaundry,
                'Needs Laundry',
                'local_laundry_service',
                'text-blue-500',
                'Mark Clean',
                handleMarkClean,
                false // no notes for laundry
            )}
        </div>
    );
};














